import * as SliderPrimitive from "@radix-ui/react-slider";
import { useTranslation } from "react-i18next";

// Piecewise-linear track: every band gets an equal share of the slider width,
// so the small bands (up to 5…50) stay easy to hit while the scale runs to 500+.
export const sliderStops = (bands) => {
  const caps = bands.filter((b) => !b.contact && b.employee_cap).map((b) => b.employee_cap);
  const maxCap = caps[caps.length - 1] || 500;
  return [1, ...caps, maxCap + 25];
};

export const maxSubscribableCap = (bands) => {
  const stops = sliderStops(bands);
  return stops[stops.length - 2];
};

const stopsToCount = (stops, v) => {
  const seg = Math.min(Math.floor(v / 100), stops.length - 2);
  const frac = v / 100 - seg;
  return Math.round(stops[seg] + frac * (stops[seg + 1] - stops[seg]));
};

const countToSlider = (stops, count) => {
  if (count <= stops[0]) return 0;
  for (let i = 0; i < stops.length - 1; i += 1) {
    if (count <= stops[i + 1]) {
      return (i + (count - stops[i]) / (stops[i + 1] - stops[i])) * 100;
    }
  }
  return (stops.length - 1) * 100;
};

export function BandSlider({ bands, count, onChange, testid = "band-slider" }) {
  const { t } = useTranslation();
  const stops = sliderStops(bands);
  const maxCap = stops[stops.length - 2];
  const ticks = [1, ...bands.filter((b) => !b.contact && b.employee_cap).map((b) => b.employee_cap), "+"];
  // Keyboard: arrows/PageUp/PageDown jump between band stops, Home/End to extremes.
  const handleKeyDown = (e) => {
    const fwd = ["ArrowRight", "ArrowUp", "PageUp"].includes(e.key);
    const back = ["ArrowLeft", "ArrowDown", "PageDown"].includes(e.key);
    let next = null;
    if (e.key === "Home") next = stops[0];
    else if (e.key === "End") next = stops[stops.length - 1];
    else if (fwd) next = stops.find((s) => s > count) ?? stops[stops.length - 1];
    else if (back) next = [...stops].reverse().find((s) => s < count) ?? stops[0];
    if (next != null) {
      e.preventDefault();
      e.stopPropagation();
      onChange(next);
    }
  };
  return (
    <div data-testid={testid}>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
          {t("bands.sliderQuestion")}
        </p>
        <p className="font-mono font-bold text-5xl tracking-tight leading-none"
          style={{ color: "var(--brand-primary)" }} data-testid={`${testid}-count`}>
          {count > maxCap ? `${maxCap}+` : count}
          <span className="text-[12px] font-sans font-medium text-slate-400 ms-2 tracking-normal align-baseline">
            {t("bands.employees")}
          </span>
        </p>
      </div>
      <div className="mt-5" onKeyDownCapture={handleKeyDown}>
        <SliderPrimitive.Root
          className="relative flex w-full touch-none select-none items-center h-5"
          min={0} max={(stops.length - 1) * 100} step={1}
          value={[countToSlider(stops, count)]}
          onValueChange={([v]) => onChange(stopsToCount(stops, v))}
          aria-label={t("bands.sliderQuestion")}
          data-testid={`${testid}-input`}>
          <SliderPrimitive.Track className="relative h-[3px] w-full grow bg-slate-200">
            <SliderPrimitive.Range className="absolute h-full" style={{ background: "var(--brand-accent)" }} />
          </SliderPrimitive.Track>
          <SliderPrimitive.Thumb
            aria-valuetext={count > maxCap ? `${maxCap}+ ${t("bands.employees")}` : `${count} ${t("bands.employees")}`}
            className="block h-5 w-5 border-2 bg-white shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 cursor-grab active:cursor-grabbing"
            style={{ borderColor: "var(--brand-accent)", "--tw-ring-color": "var(--brand-accent)" }} />
        </SliderPrimitive.Root>
      </div>
      <div className="flex justify-between mt-2 font-mono text-[9px] text-slate-400" aria-hidden="true">
        {ticks.map((s, i) => <span key={i}>{s}</span>)}
      </div>
    </div>
  );
}

export function BandReadout({ band, maxCap = 500, testid = "band-readout", children }) {
  const { t } = useTranslation();
  if (!band) return null;
  return (
    <div className="border border-slate-200 bg-white p-6" data-testid={testid}
      style={{ borderTop: "3px solid var(--brand-accent)" }}>
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
          {t("bands.yourBand")}
        </p>
        <p className="text-[14px] font-bold" style={{ color: "var(--brand-accent-ink)" }}
          data-testid={`${testid}-name`}>
          {band.name}
        </p>
      </div>
      {band.contact ? (
        <>
          <p className="font-mono font-bold text-4xl tracking-tight mt-3"
            style={{ color: "var(--brand-primary)" }} data-testid={`${testid}-price`}>
            {t("bands.contactUs")}
          </p>
          <p className="text-[12px] text-slate-500 mt-2 leading-relaxed">
            {t("bands.contactText", { count: maxCap })}
          </p>
        </>
      ) : (
        <>
          <p className="mt-3">
            <span className="font-mono font-bold text-4xl tracking-tight"
              style={{ color: "var(--brand-primary)" }} data-testid={`${testid}-price`}>
              {`$${band.usd.toLocaleString()}`}
            </span>
            <span className="text-[12px] text-slate-400 ms-1.5">{t("bands.perMonth")}</span>
          </p>
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2.5 text-[12px] text-slate-500">
            <span data-testid={`${testid}-cap`}>{t("bands.upTo", { count: band.employee_cap })}</span>
            <span data-testid={`${testid}-rate`}>{t("bands.perEmployeeRateUsd", { rate: band.per_employee_usd })}</span>
          </div>
        </>
      )}
      {children}
    </div>
  );
}
